import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SEOHead } from './SEOHead';

/**
 * FAQPageSEO - SEO component for FAQ pages
 * @param {Object} props
 * @param {string} props.pageTitle - Page title
 * @param {string} props.pageDescription - Page meta description
 * @param {string} props.canonicalUrl - Canonical URL
 * @param {Array<Object>} props.faqItems - Array of FAQ items
 * @param {string} props.faqItems[].question - FAQ question
 * @param {string} props.faqItems[].answer - FAQ answer
 * @param {string} [props.faqItems[].category] - FAQ category
 * @param {boolean} [props.faqItems[].is_published] - Publication status
 * @param {string} [props.ogImage] - Open Graph image
 * @param {string} [props.keywords] - Meta keywords
 * @param {boolean} [props.noindex=false] - Whether to add noindex tag
 */
export const FAQPageSEO = ({
  pageTitle,
  pageDescription,
  canonicalUrl,
  faqItems,
  ogImage,
  keywords,
  noindex = false,
}) => {
  // Filter only published FAQs for schema
  const publishedFaqs = faqItems.filter(faq => faq.is_published !== false);

  // FAQPage JSON-LD Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: publishedFaqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  // BreadcrumbList JSON-LD Schema
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: canonicalUrl.replace('/faq', ''),
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: 'FAQ',
        item: canonicalUrl,
      },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDescription}
        canonical={canonicalUrl}
        ogImage={ogImage}
        keywords={keywords}
        noindex={noindex}
      />
      <Helmet>
        {/* JSON-LD Schemas */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
    </>
  );
};

export default FAQPageSEO;
